import React from 'react';
import { formatCurrency } from '@utils/number';
import { formatDateToTimezone } from '@utils/date';
import { formatAddress } from '@utils/address';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { formatPhoneNumber } from '@utils/formatPhoneNumber';
import { GridLink } from '@components/gridLink/GridLink';
import { QuoteStatus, QuoteType } from '@models/quotes';

import { EnteredAddress } from '@models/validation';
import {
  PolicyGetResponse,
  PolicyTransactionSearchResponse,
  PolicyTransactionType,
  QuoteGetResponse,
  QuoteRatingSearchResponse,
} from '@lio/api-types';
import { PolicyTransactionTypes } from '@models/policyTransaction';
import { PhoneNumberType } from '@lio/rule-engine';
import { writingCompanyIds } from '@models/productOffering';
import { DetailItemProps } from './DetailItem';
import { Box, SvgIcon, Typography } from '@mui/material';
import { Button } from '@components/button';
import { Theme } from '@mui/material/styles';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { ExclamationCircleSolid } from '@assets/svgs';
import { InfiniteData } from '@tanstack/react-query';
import { Tooltip } from '@components/tooltip/Tooltip';
import { StructuredQuote } from '@store/Quote/Quote.types';
import { EditableRateVersion } from '@pages/QuoteDetails/sidebarItems/summary/EditableRateVersion';
import { EditableQuoteSource } from '@pages/QuoteDetails/sidebarItems/summary/EditableQuoteSource';
import { EditableFormVersion } from '@pages/QuoteDetails/sidebarItems/summary/EditableFormVersion';

export enum ModalType {
  RatingSummaryModal = 'RatingSummaryModal',
  TIVModal = 'TIVModal',
  KYTaxModal = 'KYTaxModal',
}

const percentageFormat = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 });

const POLICY_QUOTE_TYPES = [QuoteType.Rewrite, QuoteType.Renewal, QuoteType.Endorsement];

const PREMIUM_FIELD_QUOTE_TYPES = [
  QuoteType.Renewal,
  QuoteType.Rewrite,
  QuoteType.NewBusiness,
  QuoteType.Reinstatement,
  QuoteType.Cancelation,
];

const EXCLUDED_TRANSACTION_TYPES = [PolicyTransactionTypes.Cancelation, PolicyTransactionTypes.Reinstatement];

const formatNumber = (value: number | undefined | null) =>
  value !== undefined && value !== null ? formatCurrency(value) : '-';

const createPremiumRow = (premium: string, showWarning: boolean, WarningIcon: React.ComponentType<any>) =>
  showWarning ? (
    <Box sx={{ display: 'flex' }}>
      {premium}
      <Tooltip
        title='Quote changes were made that invalidate this estimate.'
        sx={{
          maxWidth: 300,
        }}
      >
        <SvgIcon sx={{ marginLeft: '4px', color: 'warning.main' }}>
          <WarningIcon width='16px' />
        </SvgIcon>
      </Tooltip>
    </Box>
  ) : (
    premium
  );

const createRowWithModal = (
  text: string,
  showWarning: boolean,
  WarningIcon: React.ComponentType<any>,
  toggleModalOpen: (type: ModalType, isModalOpen: boolean) => void,
  modalTitle: string,
) =>
  showWarning ? (
    <Box sx={{ display: 'flex' }}>
      {text}
      <Tooltip
        title='Quote changes were made that invalidate this estimate.'
        sx={{
          maxWidth: 300,
        }}
      >
        <SvgIcon sx={{ marginLeft: '4px', color: 'warning.main' }}>
          <WarningIcon width='16px' />
        </SvgIcon>
      </Tooltip>
    </Box>
  ) : (
    <>
      <span>{text}</span>
      <br></br>
      {createButton(() => toggleModalOpen(ModalType.RatingSummaryModal, false), modalTitle)}
    </>
  );

const createButton = (onClick: () => void, text: string, fontSize = 10) => (
  <Button
    variant='text'
    sx={{
      fontSize: ({ palette }: Theme) => `${adjustFontSize(fontSize, palette.fontSize)}px`,
      paddingRight: '0',
      paddingLeft: 0,
    }}
    onClick={onClick}
    disableRipple
  >
    {text}
  </Button>
);

const getMultiPolicyQuoteInsuranceLine = (
  multiPolicyQuoteDestinations: QuoteGetResponse['multiPolicyQuoteDestinations'],
  insuranceLine: string | null,
): QuoteGetResponse['multiPolicyQuoteDestinations'][number] | undefined => {
  return multiPolicyQuoteDestinations?.find((record: any) => record.insuranceLine?.name == insuranceLine);
};

const getFormattedPolicyNumberField = (quote: QuoteGetResponse, policy: PolicyGetResponse) => {
  const isPolicyType = POLICY_QUOTE_TYPES.includes(quote?.quoteType as QuoteType);

  if (isPolicyType) {
    return {
      label: 'Policy Number',
      value: policy ? (
        <GridLink to={`/policy/${policy.id}`}>
          {getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)}
        </GridLink>
      ) : quote?.policyNumber ? (
        <GridLink
          to={`/policy/${quote?.policyNumber.policies[0].id}`}
        >{`#${quote?.policyNumber.policyNumber}`}</GridLink>
      ) : (
        '...'
      ),
    };
  }

  return {
    label: quote?.policyId ? 'Policy Number' : 'Quote Number',
    value: quote?.policyId ? (
      <GridLink to={`/policy/${quote?.policyId}`}>{quote?.policyNumber?.policyNumber}</GridLink>
    ) : (
      `#${quote?.policyNumber?.policyNumber}`
    ),
  };
};

export const getPremiumAndFeeValues = (
  lastSuccessfulRatingPremiums: QuoteRatingSearchResponse['items'][number] | undefined,
  structuredQuote: StructuredQuote,
) => {
  const hasStructuredTaxFee =
    structuredQuote?.termTaxFeeSurcharge !== undefined &&
    structuredQuote?.termTaxFeeSurcharge !== null &&
    (lastSuccessfulRatingPremiums?.termTaxFeeSurcharge === undefined ||
      lastSuccessfulRatingPremiums?.termTaxFeeSurcharge === null);

  if (hasStructuredTaxFee) {
    const policyFee =
      structuredQuote?.termTaxFeeSurcharge && structuredQuote?.termTaxFeeSurcharge > 0
        ? structuredQuote?.termTaxFeeSurcharge
        : undefined;
    const latestTermPremium =
      structuredQuote?.termPremium ||
      (lastSuccessfulRatingPremiums?.termPremium
        ? lastSuccessfulRatingPremiums.termPremium - (policyFee ?? 0)
        : undefined);
    const latestTotalPremium =
      structuredQuote?.totalPremium ||
      (lastSuccessfulRatingPremiums?.totalPremium
        ? lastSuccessfulRatingPremiums.totalPremium - (policyFee ?? 0)
        : undefined);

    return { policyFee, latestTermPremium, latestTotalPremium };
  }

  const policyFee =
    lastSuccessfulRatingPremiums?.termTaxFeeSurcharge && lastSuccessfulRatingPremiums.termTaxFeeSurcharge > 0
      ? lastSuccessfulRatingPremiums.termTaxFeeSurcharge
      : undefined;

  return {
    policyFee,
    latestTermPremium:
      typeof lastSuccessfulRatingPremiums?.termPremium === 'number'
        ? lastSuccessfulRatingPremiums.termPremium
        : undefined,
    latestTotalPremium:
      typeof lastSuccessfulRatingPremiums?.totalPremium === 'number'
        ? lastSuccessfulRatingPremiums.totalPremium
        : undefined,
  };
};

const getBoundPremium = (policyTransactions: any, quoteId: string) => {
  if (!policyTransactions?.items.length) return null;

  const currentTransactionIndex = policyTransactions.items.findIndex(
    (transaction: any) => transaction.quote?.id === quoteId,
  );
  if (currentTransactionIndex === -1) return null;

  const transactionsToLookInto = policyTransactions.items.slice(currentTransactionIndex + 1);
  const previousTransaction = transactionsToLookInto.find(
    ({ transactionType }: any) => !EXCLUDED_TRANSACTION_TYPES.includes(transactionType as PolicyTransactionType),
  );
  return previousTransaction?.policyCostAfter ?? null;
};

const buildPremiumFields = (
  isInternalUser: boolean,
  lastSuccessfulRating: InfiniteData<QuoteRatingSearchResponse> | undefined,
  showWarningIcon: boolean,
  premiumValues: {
    policyFee: number | undefined;
    latestTermPremium: number | undefined;
    latestTotalPremium: number | undefined;
  },
  boundPremium: number | null,
  policy: PolicyGetResponse,
  isExternalAndSpecialtyAndReferred: boolean,
  multiPolicyQuoteDestinations: QuoteGetResponse['multiPolicyQuoteDestinations'],
  toggleModalOpen: (type: ModalType, isModalOpen: boolean) => void,
  quote: QuoteGetResponse,
) => {
  const isSinglePolicyQuoteDestination = !(multiPolicyQuoteDestinations && multiPolicyQuoteDestinations.length > 0);
  const isSpecialty = quote?.writingCompany?.id === writingCompanyIds.specialty;
  const isAdmitted = quote?.writingCompany?.id === writingCompanyIds.admitted;

  const hasKYJurisdiction = quote?.quoteLocations?.some(
    (location: any) => location.insuredLocation?.address?.jurisdiction?.abbreviation === 'KY',
  );

  const KY_TAX_MODAL_STATUSES = [
    QuoteStatus.Rated.queryParam,
    QuoteStatus.Underwritten.queryParam,
    QuoteStatus.ReadyToBind.queryParam,
    QuoteStatus.Bound.queryParam,
    QuoteStatus.Referred.queryParam,
    QuoteStatus.Canceled.queryParam,
    QuoteStatus.Expired.queryParam,
    QuoteStatus.Declined.queryParam,
    QuoteStatus.Rejected.queryParam,
  ];

  const showKYTaxLink =
    isInternalUser && isAdmitted && hasKYJurisdiction && KY_TAX_MODAL_STATUSES.includes(quote?.status?.name ?? '');

  const priorTermPremium =
    policy?.policyCost !== undefined && policy?.policyCost !== null ? policy?.policyCost : undefined;

  const indicationTermPremium = quote?.indicationToQuotes?.[0]?.indication?.termPremium;
  const isSubmission = quote?.status?.name === QuoteStatus.Submission.queryParam;

  const showIndicationTermPremium = isSubmission && indicationTermPremium;

  const hideEstimatedTotalPremium =
    isExternalAndSpecialtyAndReferred ||
    (!isSinglePolicyQuoteDestination && isSpecialty) ||
    quote?.quoteType === QuoteType.Endorsement;

  const baseFields = [
    {
      label: 'Indication Premium',
      value: createPremiumRow(formatNumber(indicationTermPremium), showWarningIcon, ExclamationCircleSolid),
      restrictQuoteTypes: PREMIUM_FIELD_QUOTE_TYPES,
      hidden: !showIndicationTermPremium,
    },
    {
      label: 'Estimated Total Premium',
      value:
        isInternalUser && Number.isFinite(lastSuccessfulRating?.pages[0].items?.[0]?.termPremium)
          ? createRowWithModal(
              formatNumber(premiumValues.latestTermPremium),
              showWarningIcon,
              ExclamationCircleSolid,
              toggleModalOpen,
              'View Rating Details',
            )
          : createPremiumRow(formatNumber(premiumValues.latestTermPremium), showWarningIcon, ExclamationCircleSolid),
      restrictQuoteTypes: PREMIUM_FIELD_QUOTE_TYPES,
      hidden: hideEstimatedTotalPremium || showIndicationTermPremium,
    },
    {
      label: 'Taxes, Fees, and Surcharges',
      value: showKYTaxLink ? (
        <>
          <span>
            {formatNumber(quote?.termTaxFeeSurcharge ?? lastSuccessfulRating?.pages[0].items?.[0]?.termTaxFeeSurcharge)}
          </span>
          <br />
          {createButton(() => toggleModalOpen(ModalType.KYTaxModal, false), 'View KY Taxes, Fees, and Surcharges')}
        </>
      ) : (
        formatNumber(quote?.termTaxFeeSurcharge ?? lastSuccessfulRating?.pages[0].items?.[0]?.termTaxFeeSurcharge)
      ),
      restrictQuoteTypes: PREMIUM_FIELD_QUOTE_TYPES,
      hidden: !isAdmitted || !isSinglePolicyQuoteDestination || hideEstimatedTotalPremium,
    },
    {
      label: 'Policy Fee',
      value: premiumValues.policyFee !== undefined ? formatCurrency(premiumValues.policyFee) : undefined,
      hidden: isExternalAndSpecialtyAndReferred || !isSpecialty || (!isSinglePolicyQuoteDestination && isSpecialty),
    },
    {
      label: 'Bound Premium',
      value: boundPremium !== null ? formatCurrency(boundPremium, { minimumPrecision: 2 }) : '-',
      restrictQuoteTypes: [QuoteType.Endorsement],
    },
    {
      label: 'New Annualized Premium',
      // NOTE: for endorsement quotes this should use total premium
      value:
        isInternalUser && Number.isFinite(lastSuccessfulRating?.pages[0].items?.[0]?.totalPremium)
          ? createRowWithModal(
              formatNumber(premiumValues.latestTotalPremium),
              showWarningIcon,
              ExclamationCircleSolid,
              toggleModalOpen,
              'View Rating Details',
            )
          : createPremiumRow(formatNumber(premiumValues.latestTotalPremium), showWarningIcon, ExclamationCircleSolid),
      restrictQuoteTypes: [QuoteType.Endorsement],
    },
    {
      label: 'Package Premium',
      value: createPremiumRow(
        formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.termPremium),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden:
        isSinglePolicyQuoteDestination ||
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null) ||
        isSpecialty,
    },
    {
      label: 'Package Taxes, Fees, and Surcharges',
      value: formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.termTaxFeeSurcharge),
      hidden:
        !isAdmitted ||
        isSinglePolicyQuoteDestination ||
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null),
    },
    {
      label: 'Excess Liability Premium',
      value: createPremiumRow(
        formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.termPremium),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden: !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability') || isSpecialty,
    },
    {
      label: 'Excess Liability Taxes, Fees, and Surcharges',
      value: formatNumber(
        getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.termTaxFeeSurcharge,
      ),
      hidden: !isAdmitted || !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability'),
    },
    {
      label: 'Management Liability Premium',
      value: createPremiumRow(
        formatNumber(
          getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Management Liability')?.termPremium,
        ),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden:
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Management Liability') ||
        isSinglePolicyQuoteDestination ||
        isSpecialty,
    },
    {
      label: 'Management Liability Taxes, Fees, and Surcharges',
      value: formatNumber(
        getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Management Liability')?.termTaxFeeSurcharge,
      ),
      hidden:
        !isAdmitted ||
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Management Liability') ||
        isSinglePolicyQuoteDestination,
    },
    {
      label: 'Accident Premium',
      value: createPremiumRow(
        formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Accident')?.termPremium),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden:
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Accident') ||
        isSinglePolicyQuoteDestination ||
        isSpecialty,
    },
    {
      label: 'Accident Taxes, Fees, and Surcharges',
      value: formatNumber(
        getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Accident')?.termTaxFeeSurcharge,
      ),
      hidden:
        !isAdmitted ||
        !getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Accident') ||
        isSinglePolicyQuoteDestination,
    },
    {
      label: 'Primary General Liability Premium',
      value: createPremiumRow(
        formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.termPremium),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden:
        isExternalAndSpecialtyAndReferred ||
        typeof getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.termPremium !== 'number' ||
        isSinglePolicyQuoteDestination ||
        !isSpecialty,
    },
    {
      label: 'Primary General Liability Policy Fee',
      value:
        premiumValues.policyFee !== undefined
          ? formatCurrency(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.policyFee)
          : undefined,
      hidden:
        isExternalAndSpecialtyAndReferred ||
        isSinglePolicyQuoteDestination ||
        !isSpecialty ||
        typeof getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, null)?.policyFee !== 'number',
    },
    {
      label: 'Excess Liability Premium',
      value: createPremiumRow(
        formatNumber(getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.termPremium),
        showWarningIcon,
        ExclamationCircleSolid,
      ),
      hidden:
        isExternalAndSpecialtyAndReferred ||
        isSinglePolicyQuoteDestination ||
        !isSpecialty ||
        typeof getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.termPremium !==
          'number',
    },
    {
      label: 'Excess Liability Policy Fee',
      value:
        premiumValues.policyFee !== undefined
          ? formatCurrency(
              getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.policyFee,
            )
          : undefined,
      hidden:
        isExternalAndSpecialtyAndReferred ||
        isSinglePolicyQuoteDestination ||
        !isSpecialty ||
        typeof getMultiPolicyQuoteInsuranceLine(multiPolicyQuoteDestinations, 'Excess Liability')?.policyFee !==
          'number',
    },
    {
      label: 'New Policy Premium',
      value:
        lastSuccessfulRating?.pages[0].items?.[0]?.endorsementDetails?.newPolicyCost !== undefined
          ? createPremiumRow(
              formatCurrency(lastSuccessfulRating.pages[0].items[0].endorsementDetails.newPolicyCost),
              showWarningIcon,
              ExclamationCircleSolid,
            )
          : '-',
      restrictQuoteTypes: [QuoteType.Endorsement],
    },
    {
      label: 'Endorsement Premium Change',
      value: lastSuccessfulRating?.pages[0].items?.[0]?.endorsementDetails?.endorsementPremiumChange
        ? createPremiumRow(
            formatCurrency(lastSuccessfulRating.pages[0].items[0].endorsementDetails.endorsementPremiumChange),
            showWarningIcon,
            ExclamationCircleSolid,
          )
        : '-',
      restrictQuoteTypes: [QuoteType.Endorsement],
    },
    {
      label: 'Prior Term Premium',
      value:
        isInternalUser && policy?.policyCost !== undefined && policy?.policyCost !== null ? (
          <>
            <span>{formatNumber(priorTermPremium) ?? '-'}</span>
            <br></br>
            <Button
              variant='text'
              sx={{ fontSize: ({ palette }: Theme) => `${adjustFontSize(10, palette.fontSize)}px`, paddingX: '0' }}
              target='_blank'
              href={`/quote/${quote?.renewalSourceQuoteId}?sidebarItem=summary&sidebarTab=details&ratingDetails=true`}
              disableRipple
            >
              View Prior Term Details
            </Button>
          </>
        ) : (
          createPremiumRow(formatNumber(priorTermPremium), showWarningIcon, ExclamationCircleSolid)
        ),
      restrictQuoteTypes: [QuoteType.Renewal, QuoteType.Rewrite, QuoteType.Reinstatement, QuoteType.Cancelation],
    },
    {
      label: '',
      value: <>{createButton(() => toggleModalOpen(ModalType.RatingSummaryModal, false), 'View Rating Details')}</>,
      restrictQuoteTypes: PREMIUM_FIELD_QUOTE_TYPES,
      hidden: !isInternalUser || !hideEstimatedTotalPremium,
    },
  ];

  return baseFields.filter((field) => !field.hidden && field.value !== undefined);
};

export type QuoteSummaryData = {
  quoteDetails: {
    label: string;
    fields: DetailItemProps[];
  };
  customerDetails: {
    label: string;
    fields: DetailItemProps[];
  };
  partnerAgent: {
    label: string;
    fields: DetailItemProps[];
  };
  customerService: {
    label: string;
    fields: DetailItemProps[];
  };
  showWarningIcon: boolean;
  isExternalAndSpecialtyAndReferred: boolean;
  premiumValues: {
    policyFee: number | undefined | null;
    latestTermPremium: number | undefined;
    latestTotalPremium: number | undefined;
  };
};

export const getQuoteSummaryData = (
  quote: QuoteGetResponse,
  structuredQuote: StructuredQuote,
  policy: PolicyGetResponse,
  lastSuccessfulRating: InfiniteData<QuoteRatingSearchResponse> | undefined,
  policyTransactions: PolicyTransactionSearchResponse,
  isInternalUser: boolean,
  toggleModalOpen: (type: ModalType, isModalOpen: boolean) => void,
): QuoteSummaryData => {
  const quoteId = quote?.id;
  const quoteType = quote?.quoteType as QuoteType;
  const isEndorsement = quoteType === QuoteType.Endorsement;
  const isRenewal = quoteType === QuoteType.Renewal;
  const isRewrite = quoteType === QuoteType.Rewrite;

  const showWarningIcon = Boolean(lastSuccessfulRating?.pages[0].items[0]) && !Number.isFinite(quote?.totalPremium);
  const isExternalAndSpecialtyAndReferred =
    !isInternalUser &&
    quote?.writingCompany?.id === writingCompanyIds.specialty &&
    quote?.status?.id === QuoteStatus.Referred.id;

  const boundPremium = getBoundPremium(policyTransactions, quoteId);
  const policyQuoteItem = structuredQuote?.quoteItems?.find(
    (qi) => qi.quoteItemType.codeName === 'Policy',
  )?.attributeTypes;
  const namedInsuredQuoteItem = structuredQuote?.quoteItems?.find(
    (qi) => qi.quoteItemType.codeName === 'NamedInsured',
  )?.attributeTypes;

  const insuredAddresses = quote?.quoteLocations?.map((item: any, index: number) => (
    <Box
      key={item.insuredLocation.address.id}
      style={{ marginBottom: index === quote?.quoteLocations?.length - 1 ? 0 : '8px' }}
    >
      <Typography variant='bodySmall'>{formatAddress(item.insuredLocation.address)}</Typography>
    </Box>
  ));

  const billingAddress = {
    street1: policyQuoteItem?.BillingStreet?.attribute?.value,
    street2: policyQuoteItem?.BillingStreet2?.attribute?.value,
    city: policyQuoteItem?.BillingCity?.attribute?.value,
    state: policyQuoteItem?.BillingState?.attribute?.value,
    zipCode: policyQuoteItem?.BillingZip?.attribute?.value,
  };

  const customerMailingAddress = {
    street1: namedInsuredQuoteItem?.MailingStreet?.attribute?.value,
    street2: namedInsuredQuoteItem?.MailingStreet2.attribute?.value,
    city: namedInsuredQuoteItem?.MailingCity.attribute?.value,
    state: namedInsuredQuoteItem?.MailingState.attribute?.value,
    zipCode: namedInsuredQuoteItem?.MailingZip.attribute?.value,
  };

  const lastSuccessfulRatingPremiums = lastSuccessfulRating?.pages[0].items?.[0];
  const premiumValues = getPremiumAndFeeValues(lastSuccessfulRatingPremiums, structuredQuote);

  const totalInsurableValue = policyQuoteItem?.CPTIV?.attribute?.value as number;
  const loiPropertyQuoted = policyQuoteItem?.LOIPropertyInd?.attribute?.value;

  const policyEffectiveDate =
    policyQuoteItem?.PolicyEffectiveDate.attribute?.valueOverride ??
    policyQuoteItem?.PolicyEffectiveDate.attribute?.value;
  const policyExpirationDate =
    policyQuoteItem?.PolicyExpirationDate.attribute?.valueOverride ??
    policyQuoteItem?.PolicyExpirationDate.attribute?.value;
  const policyInceptionOnDate = quote?.policyNumber?.policies?.sort(
    (
      a: NonNullable<QuoteGetResponse['policyNumber']>['policies'][number],
      b: NonNullable<QuoteGetResponse['policyNumber']>['policies'][number],
    ) => {
      if (!a.createdAt || !b?.createdAt) return 0;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    },
  )[0]?.inceptionOn;

  const user = quote?.brokerageProducer.user;
  const uw = quote?.underwriter.user;
  const underwriterPhoneNumber =
    uw?.phoneNumbers?.find((phone: any) => phone.type === PhoneNumberType.Main)?.number ??
    uw?.phoneNumbers?.[0]?.number;

  const premiumFields = buildPremiumFields(
    isInternalUser,
    lastSuccessfulRating,
    showWarningIcon,
    premiumValues,
    boundPremium,
    policy,
    isExternalAndSpecialtyAndReferred,
    quote?.multiPolicyQuoteDestinations,
    toggleModalOpen,
    quote,
  );

  const quoteDetailsFields: DetailItemProps[] = [
    getFormattedPolicyNumberField(quote, policy),
    { label: 'Quote Status', value: quote?.status.label },
  ];

  if (isInternalUser) {
    quoteDetailsFields.push({
      label: '',
      value: <EditableQuoteSource quoteId={quoteId} reportingQuoteSource={quote?.reportingQuoteSource} />,
    });
  }

  if (isInternalUser && quote?.quoteSource?.type === 'API') {
    quoteDetailsFields.push({ label: 'Quote Origin', value: quote?.quoteSource?.name });
  }

  quoteDetailsFields.push({ label: 'Product', value: quote?.offering.label || quote?.offering.insuranceProduct.name });

  if (quote?.productLabel?.name) {
    quoteDetailsFields.push({ label: 'Product Label', value: quote?.productLabel.name });
  }
  if (quote?.productOption?.name) {
    quoteDetailsFields.push({ label: 'Business Class', value: quote?.productOption.name });
  }

  quoteDetailsFields.push({ label: 'Insured Address(es)', value: insuredAddresses });

  if (isEndorsement || isRewrite) {
    quoteDetailsFields.push({
      label: 'Transaction Effective Date',
      value: quote?.effectiveOn && formatDateToTimezone(quote.effectiveOn),
    });
  }
  if (isRenewal) {
    quoteDetailsFields.push({
      label: 'Policy Inception Date',
      value: policyInceptionOnDate && formatDateToTimezone(policyInceptionOnDate),
    });
  }
  if (isRenewal && isInternalUser) {
    const complianceReviewDate =
      quote?.policyNumber?.policies &&
      [...quote.policyNumber.policies].sort(
        (
          a: NonNullable<QuoteGetResponse['policyNumber']>['policies'][number],
          b: NonNullable<QuoteGetResponse['policyNumber']>['policies'][number],
        ) => {
          if (!a.createdAt || !b?.createdAt) return 0;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        },
      )[0]?.complianceReviewDate;
    quoteDetailsFields.push({
      label: 'Compliance Review Date',
      value: complianceReviewDate ? formatDateToTimezone(complianceReviewDate) : null,
    });
  }

  quoteDetailsFields.push(
    {
      label: isRenewal ? 'New Term Effective Date' : 'Policy Effective Date',
      value: policyEffectiveDate && formatDateToTimezone(policyEffectiveDate as string),
    },
    {
      label: 'Policy Expiration Date',
      value: policyExpirationDate && formatDateToTimezone(policyExpirationDate as string),
    },
  );

  if (loiPropertyQuoted) {
    quoteDetailsFields.push({
      label: 'Property TIV',
      value: formatNumber(totalInsurableValue),
      suffix:
        isInternalUser && totalInsurableValue !== undefined && totalInsurableValue !== null
          ? createButton(() => toggleModalOpen(ModalType.TIVModal, false), 'See TIV Details')
          : null,
    });
  }

  premiumFields
    .filter((field: any) => !field.restrictQuoteTypes || field.restrictQuoteTypes.includes(quoteType))
    .forEach((field: any) => quoteDetailsFields.push(field as DetailItemProps));
  // Additional fields for internal users only (at the very bottom)
  if (isInternalUser) {
    if (quote?.quoteType === QuoteType.Renewal) {
      quoteDetailsFields.push({
        label: 'Target Rate Change',
        value:
          quote.renewalTargetRateChange === null || quote.renewalTargetRateChange === undefined
            ? '-'
            : percentageFormat.format(quote.renewalTargetRateChange),
      });
    }
    quoteDetailsFields.push({
      label: '',
      value: <EditableRateVersion quoteId={quoteId} rateVersion={quote?.rateVersion} />,
    });
    if (quote?.formsV2Enabled === true) {
      quoteDetailsFields.push({
        label: '',
        value: <EditableFormVersion quoteId={quoteId} formsVersion={quote?.formsVersion} />,
      });
    }
  }

  const quoteDetails = {
    label: 'Quote Details',
    fields: quoteDetailsFields,
  };

  const customerDetails = {
    label: 'Customer Details',
    fields: [
      {
        label: 'Customer Name',
        value: isInternalUser ? (
          <GridLink to={`/customer/${quote?.customer.id}`} rel='noopener noreferrer'>
            {quote?.customer.name}
          </GridLink>
        ) : (
          quote?.customer.name
        ),
      },
      { label: 'Phone Number', value: namedInsuredQuoteItem?.CustomerPhone?.attribute?.value ?? '-' },
      { label: 'Email Address', value: namedInsuredQuoteItem?.CustomerEmail?.attribute?.value ?? '-' },
      { label: 'Mailing Address', value: formatAddress(customerMailingAddress as EnteredAddress) },
      { label: 'Billing Address', value: formatAddress(billingAddress as EnteredAddress) },
    ] as DetailItemProps[],
  };

  const partnerAgent = {
    label: 'Brokerage',
    fields: [
      { label: 'Brokerage', value: quote?.brokerage.name },
      { label: 'Producer', value: `${user?.firstName} ${user?.lastName}` },
      { label: 'Email Address', value: user?.email },
    ] as DetailItemProps[],
  };

  const customerService = {
    label: 'Customer Service',
    fields: [
      { label: 'Assigned Underwriter', value: `${uw?.firstName} ${uw?.lastName}` },
      { label: 'Email', value: uw?.email ?? 'service@lioinsurance.com' },
      {
        label: 'Phone Number',
        value: underwriterPhoneNumber ? formatPhoneNumber(underwriterPhoneNumber) : '1 (877) 546-7155',
      },
    ] as DetailItemProps[],
  };

  return {
    quoteDetails,
    customerDetails,
    partnerAgent,
    customerService,
    showWarningIcon,
    isExternalAndSpecialtyAndReferred,
    premiumValues,
  };
};
